/**
 * Shared helpers for the Huskee custom-order page and cart artwork replace.
 * File uploads go to Shopify via multipart /cart/add.js and /cart/change.js.
 */
(function (window) {
  'use strict';

  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var MAX_BYTES = 20 * 1024 * 1024;
  var ALLOWED_EXT = ['pdf', 'ai', 'eps', 'svg', 'png', 'jpg', 'jpeg'];
  var IMAGE_EXT = ['png', 'jpg', 'jpeg', 'svg', 'gif', 'webp'];
  var PDF_JS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDF_JS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  function fileExt(name) {
    var parts = String(name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  function validateFile(file) {
    if (!file) return 'Choose an artwork file.';
    if (file.size > MAX_BYTES) return 'File must be 20 MB or smaller.';
    var ext = fileExt(file.name);
    if (ALLOWED_EXT.indexOf(ext) === -1) {
      return 'Use a PDF, AI, EPS, SVG, PNG or JPG file.';
    }
    return '';
  }

  function revokeUrl(el) {
    var prev = el && el.getAttribute('data-object-url');
    if (prev) {
      URL.revokeObjectURL(prev);
      el.removeAttribute('data-object-url');
    }
  }

  function loadPdfJs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = PDF_JS_SRC;
      s.onload = function () {
        if (!window.pdfjsLib) {
          reject(new Error('PDF.js failed to load'));
          return;
        }
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER;
        resolve(window.pdfjsLib);
      };
      s.onerror = function () { reject(new Error('PDF.js failed to load')); };
      document.head.appendChild(s);
    });
  }

  function previewFile(file, pane) {
    if (!pane) return;
    revokeUrl(pane);
    pane.innerHTML = '';
    var err = validateFile(file);
    if (err) {
      pane.innerHTML = '<p class="husk-art-preview__msg">' + escapeHtml(err) + '</p>';
      return;
    }
    var ext = fileExt(file.name);
    var info = '<p class="husk-art-preview__name">' + escapeHtml(file.name) +
      ' <span>(' + Math.round(file.size / 1024) + ' KB)</span></p>';

    if (IMAGE_EXT.indexOf(ext) !== -1) {
      var url = URL.createObjectURL(file);
      pane.setAttribute('data-object-url', url);
      pane.innerHTML = info + '<img class="husk-art-preview__img" alt="Artwork preview" src="' + url + '">';
      return;
    }
    if (ext === 'pdf') {
      pane.innerHTML = info + '<canvas class="husk-art-preview__canvas"></canvas>';
      var canvas = pane.querySelector('canvas');
      loadPdfJs().then(function (pdfjsLib) {
        var objUrl = URL.createObjectURL(file);
        pane.setAttribute('data-object-url', objUrl);
        return pdfjsLib.getDocument(objUrl).promise;
      }).then(function (pdf) {
        return pdf.getPage(1);
      }).then(function (page) {
        var viewport = page.getViewport({ scale: 1 });
        var scale = Math.min(320 / viewport.width, 1.4);
        var scaled = page.getViewport({ scale: scale });
        canvas.width = scaled.width;
        canvas.height = scaled.height;
        return page.render({ canvasContext: canvas.getContext('2d'), viewport: scaled }).promise;
      }).catch(function () {
        pane.innerHTML = info + '<p class="husk-art-preview__msg">PDF preview unavailable — the file will still be attached.</p>';
      });
      return;
    }
    pane.innerHTML = info +
      '<p class="husk-art-preview__msg">We can\'t preview AI/EPS in the browser. We\'ll email a digital proof before production.</p>';
  }

  function artworkViewHtml(url, filename) {
    if (!url) return '';
    var name = filename || decodeURIComponent(String(url).split('/').pop() || 'Artwork');
    var ext = fileExt(name.split('?')[0]);
    var safeUrl = escapeAttr(url);
    var safeName = escapeHtml(name);
    if (IMAGE_EXT.indexOf(ext) !== -1) {
      return '<div class="husk-line-art">' +
        '<a class="husk-line-art__thumb" href="' + safeUrl + '" target="_blank" rel="noopener">' +
        '<img src="' + safeUrl + '" alt="Uploaded artwork">' +
        '</a><a class="husk-line-art__link" href="' + safeUrl + '" target="_blank" rel="noopener">View ' + safeName + '</a></div>';
    }
    return '<div class="husk-line-art">' +
      '<a class="husk-line-art__link" href="' + safeUrl + '" target="_blank" rel="noopener">View artwork: ' + safeName + '</a></div>';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }

  function propertiesObject(raw) {
    var o = {};
    if (Array.isArray(raw)) {
      raw.forEach(function (pair) {
        if (Array.isArray(pair) && pair[0] != null) o[String(pair[0])] = pair[1];
      });
    } else if (raw && typeof raw === 'object') {
      Object.keys(raw).forEach(function (k) { o[k] = raw[k]; });
    }
    return o;
  }

  function appendProps(form, props, skipKey) {
    Object.keys(props || {}).forEach(function (key) {
      if (key === skipKey) return;
      if (props[key] == null || props[key] === '') return;
      form.append('properties[' + key + ']', String(props[key]));
    });
  }

  function cartAddForm(variantId, quantity, props, file, filePropName) {
    var form = new FormData();
    form.append('id', String(variantId));
    form.append('quantity', String(quantity));
    appendProps(form, props, filePropName);
    if (file && filePropName) {
      form.append('properties[' + filePropName + ']', file, file.name);
    }
    return fetch(routesRoot + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: form
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var msg = (body && (body.description || body.message)) || 'Could not add to basket.';
          throw new Error(msg);
        }
        return body;
      });
    });
  }

  function cartAddJson(items) {
    return fetch(routesRoot + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var msg = (body && (body.description || body.message)) || 'Could not add cups to basket.';
          throw new Error(msg);
        }
        return body;
      });
    });
  }

  function cartChangeJson(payload) {
    return fetch(routesRoot + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) {
          var msg = (body && (body.description || body.message)) || 'Could not update basket.';
          throw new Error(msg);
        }
        return body;
      });
    });
  }

  function getCart() {
    return fetch(routesRoot + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); });
  }

  function clearExistingJobs() {
    return getCart().then(function (cart) {
      var items = (cart.items || []).filter(function (item) {
        return item.properties && item.properties._custom_job;
      });
      var chain = Promise.resolve();
      items.forEach(function (item) {
        chain = chain.then(function () {
          return cartChangeJson({
            id: item.key,
            quantity: 0,
            properties: propertiesObject(item.properties)
          });
        });
      });
      return chain;
    });
  }

  function isPrintLine(item) {
    var p = item.properties || {};
    return !!(p.Artwork || p._customisation_extra);
  }

  function physicalCupQuantity(item) {
    var props = (item && item.properties) || {};
    var packSize = parseInt(props._cups_per_pack, 10);
    if (isNaN(packSize) || packSize < 1) packSize = 1;
    return (Number(item && item.quantity) || 0) * packSize;
  }

  function syncPrintQuantities() {
    return getCart().then(function (cart) {
      var jobs = {};
      (cart.items || []).forEach(function (item) {
        var job = item.properties && item.properties._custom_job;
        if (!job) return;
        if (!jobs[job]) jobs[job] = { cups: 0, print: null };
        if (isPrintLine(item)) jobs[job].print = item;
        else jobs[job].cups += physicalCupQuantity(item);
      });
      var changed = false;
      var chain = Promise.resolve();
      Object.keys(jobs).forEach(function (jobId) {
        var job = jobs[jobId];
        if (!job.print) return;
        var want = job.cups;
        if (want <= 0) {
          changed = true;
          chain = chain.then(function () {
            return cartChangeJson({
              id: job.print.key,
              quantity: 0,
              properties: propertiesObject(job.print.properties)
            });
          });
        } else if (job.print.quantity !== want) {
          changed = true;
          chain = chain.then(function () {
            return cartChangeJson({
              id: job.print.key,
              quantity: want,
              properties: propertiesObject(job.print.properties)
            });
          });
        }
      });
      return chain.then(function () { return changed; });
    });
  }

  function replaceArtwork(lineKey, file, existingProps, quantity, variantId) {
    var err = validateFile(file);
    if (err) return Promise.reject(new Error(err));
    var props = propertiesObject(existingProps);
    var form = new FormData();
    form.append('id', String(lineKey));
    form.append('quantity', String(quantity || 1));
    appendProps(form, props, 'Artwork');
    form.append('properties[Artwork]', file, file.name);
    return fetch(routesRoot + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: form
    }).then(function (res) {
      return res.json().then(function (body) {
        if (res.ok) return body;
        var vid = variantId || props._print_variant_id;
        if (!vid) {
          throw new Error('Could not replace artwork. Remove the print line and place the order again.');
        }
        return cartChangeJson({
          id: lineKey,
          quantity: 0,
          properties: props
        }).then(function () {
          var addProps = Object.assign({}, props);
          delete addProps.Artwork;
          return cartAddForm(vid, quantity || 1, addProps, file, 'Artwork');
        });
      });
    });
  }

  function unitPence(variant, qty) {
    var price = Number(variant.unit_pence) || 0;
    var breaks = (variant.breaks || []).slice();

    /* Match cart-contents exactly. Its quantity-break data can arrive in an
    inconsistent object order, so the basket sorts thresholds upwards and
    prices downwards independently before matching them. Keeping each raw
    threshold/price pair together can apply a cheaper tier too early. */
    var quantities = breaks.map(function (row) {
      return Number(row.min || 0);
    }).sort(function (a, b) { return a - b; });

    var prices = breaks.filter(function (row) {
      return row.pence != null;
    }).map(function (row) {
      return Number(row.pence);
    }).sort(function (a, b) { return b - a; });

    var count = Math.min(quantities.length, prices.length);
    for (var i = 0; i < count; i += 1) {
      if (qty >= quantities[i]) price = prices[i];
      else break;
    }
    return price;
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format || window.themeMoneyFormat);
    }
    return '£' + (Number(cents || 0) / 100).toFixed(2);
  }

  window.HuskeeCustomJob = {
    MAX_BYTES: MAX_BYTES,
    validateFile: validateFile,
    previewFile: previewFile,
    artworkViewHtml: artworkViewHtml,
    propertiesObject: propertiesObject,
    cartAddForm: cartAddForm,
    cartAddJson: cartAddJson,
    cartChangeJson: cartChangeJson,
    getCart: getCart,
    clearExistingJobs: clearExistingJobs,
    syncPrintQuantities: syncPrintQuantities,
    replaceArtwork: replaceArtwork,
    isPrintLine: isPrintLine,
    physicalCupQuantity: physicalCupQuantity,
    unitPence: unitPence,
    formatMoney: formatMoney
  };
})(window);
